// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { Chip } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { ShieldCheck, AlertTriangle } from "lucide-react"

import { isDeliverableConfidence } from "@crm-src/lib/ai-prospect-recipients"
import type { EmailConfidence } from "@crm-src/types/lead"

/**
 * Recipient-type chip styling + the AI-prospect confidence chip for the email
 * target selector, extracted from `email-target-selector.tsx`. No money /
 * reseller here — wall-neutral.
 */

/** Maps a colored recipient type to a semantic MUI palette key. `manual` is
 * intentionally absent — it renders neutral grey. */
export const TYPE_PALETTE: Record<string, "primary" | "success" | "secondary" | "info"> = {
  customer: "primary",
  prospect: "success",
  contact: "secondary",
  ai_prospect: "info",
}

/** Like {@link TYPE_PALETTE} but for the `<Chip color>` prop, which also accepts
 * `default` (used for `manual` recipients). */
export const TYPE_CHIP_COLOR: Record<string, "primary" | "success" | "secondary" | "info" | "default"> = {
  customer: "primary",
  prospect: "success",
  contact: "secondary",
  ai_prospect: "info",
  manual: "default",
}

/** Tinted background + border for a selected row of the given type; neutral
 * (`surfaceSoft` / `divider`) for an unknown type (e.g. `manual`). */
export function typeRowSx(type: string) {
  const pal = TYPE_PALETTE[type]
  if (!pal) {
    return { bgcolor: "surfaceSoft", borderColor: "divider" }
  }
  return {
    bgcolor: (t: any) => alpha(t.palette[pal].main, 0.08),
    borderColor: (t: any) => alpha(t.palette[pal].main, 0.3),
  }
}

/** Verified/likely → green "Verified"; everything else (guessed/unknown/null) →
 * amber "Unverified". Only rendered for AI-prospect rows. */
export function ConfidenceChip({ confidence }: { confidence: EmailConfidence | null | undefined }) {
  const deliverable = isDeliverableConfidence(confidence)
  return (
    <Chip
      size="small"
      color={deliverable ? "success" : "warning"}
      variant="outlined"
      icon={deliverable ? <ShieldCheck size={11} /> : <AlertTriangle size={11} />}
      label={deliverable ? "Verified" : "Unverified"}
      sx={{ fontSize: 10.5, height: 18, flexShrink: 0, "& .MuiChip-label": { px: 0.5 }, "& .MuiChip-icon": { ml: 0.5 } }}
    />
  )
}
